import React, { useEffect, useRef } from 'react';
import { Field, FieldWrapper } from '@progress/kendo-react-form';
import { Button } from '@progress/kendo-react-buttons';
import { Checkbox, InputSuffix } from '@progress/kendo-react-inputs';
import { DateTimePicker } from '@progress/kendo-react-dateinputs';
import { Label } from '@progress/kendo-react-labels';
import { chevronDownIcon } from '@progress/kendo-svg-icons';
import type { AnomalyBandWithForm } from './AnomalyBand/types';
import type { OptionItem } from './AnomalyDetectorForm.utils';
import {
  CONFIDENCE_OPTIONS,
  CRITICALITY_OPTIONS,
  criticalityRequiredValidator,
  endDateTimeValidator,
  FIELD_SPACING,
  getLocalizedStreamOptions,
  isFutureDate,
  startDateTimeValidator,
  streamsRequiredValidator,
} from './AnomalyDetectorForm.utils';
import {
  FormDateTimePicker,
  FormMultiSelect,
  FormNumericTextBox,
  FormSegmentedOptions,
} from '../../../../../../Shared/Components/Forms/FormsComponents';
import { enMessages } from '../../../../../../../messages/en-US';

interface AnomalyDetailsFormProps {
  formRenderProps: any;
  selectedBandId: string | null;
  selectedBand: AnomalyBandWithForm | undefined;
  readOnly?: boolean;
  formatPeriod: (unixSeconds: number) => string;
  syncFormToBands: (data: any) => void;
  localizationService: any;
  onValidityChange?: (valid: boolean) => void;
  criticalityOptions?: OptionItem[];
  indicatorOptions?: OptionItem[];
  rootCauseOptions?: OptionItem[];
  actionOptions?: OptionItem[];
  onIndicatorCustomAdded?: (item: OptionItem) => void;
  onRootCauseCustomAdded?: (item: OptionItem) => void;
  onActionCustomAdded?: (item: OptionItem) => void;
}

const renderItemWithCheckbox = (li: React.ReactElement, itemProps: any) => {
  const itemValue = String(itemProps.dataItem?.value ?? itemProps.dataItem?.text ?? '');
  const itemText = String(itemProps.dataItem?.text ?? itemProps.dataItem?.value ?? '');
  const itemChildren = (
    <span>
      <Checkbox
        name={itemValue}
        checked={itemProps.selected}
        onChange={() => {
          const syntheticEvent = {
            stopPropagation: () => {},
            preventDefault: () => {},
            target: { checked: !itemProps.selected },
          };
          itemProps.onClick(itemProps.index, syntheticEvent);
        }}
      >
        <Label className="k-checkbox-label">&nbsp;{itemText}</Label>
      </Checkbox>
    </span>
  );

  return React.cloneElement(li, li.props, itemChildren);
};

export default function AnomalyDetailsForm({
  formRenderProps,
  selectedBandId,
  selectedBand,
  readOnly = false,
  formatPeriod,
  syncFormToBands,
  localizationService,
  onValidityChange,
  criticalityOptions = CRITICALITY_OPTIONS,
  indicatorOptions = [],
  rootCauseOptions = [],
  actionOptions = [],
  onIndicatorCustomAdded,
  onRootCauseCustomAdded,
  onActionCustomAdded,
}: AnomalyDetailsFormProps): React.ReactElement {
  const prevValuesRef = useRef<string>('');
  const lastPeriodSyncedFromFormRef = useRef<{ startTime: number; endTime: number } | null>(null);
  const localizedStreamOptions = getLocalizedStreamOptions(localizationService);
  const normalizedIndicatorOptions = indicatorOptions
    .filter((item) => item && (item.value != null || item.text != null))
    .map((item) => ({
      text: String(item.text ?? item.value ?? ''),
      value: String(item.value ?? item.text ?? ''),
    }));
  const normalizedRootCauseOptions = rootCauseOptions
    .filter((item) => item && (item.value != null || item.text != null))
    .map((item) => ({
      text: String(item.text ?? item.value ?? ''),
      value: String(item.value ?? item.text ?? ''),
    }));
  const normalizedActionOptions = actionOptions
    .filter((item) => item && (item.value != null || item.text != null))
    .map((item) => ({
      text: String(item.text ?? item.value ?? ''),
      value: String(item.value ?? item.text ?? ''),
    }));
  const isDisabled = !selectedBandId || readOnly;
  const isAnchorBand = selectedBand?.bandType === 'anchor';
  const startDateTimeValue = formRenderProps.valueGetter('startDateTime');
  const endDateTimeValue = formRenderProps.valueGetter('endDateTime');
  const streamsValue = formRenderProps.valueGetter('streams');
  const criticalityValue = formRenderProps.valueGetter('criticality');
  const indicatorsValue = formRenderProps.valueGetter('indicators');
  const rootCausesValue = formRenderProps.valueGetter('rootCauses');
  const actionsValue = formRenderProps.valueGetter('actions');
  const confidenceValue = formRenderProps.valueGetter('confidence');
  const savingsValue = formRenderProps.valueGetter('savings');
  const ongoingValue = formRenderProps.valueGetter('ongoing') === true;
  const selectedBandStartTime = selectedBand?.startTime;
  const selectedBandEndTime = selectedBand?.endTime;
  // Anomalies are historical — the period can never extend past the current date/time.
  const now = new Date();
  const isValidDateValue = (value: unknown): value is Date =>
    value instanceof Date && !Number.isNaN(value.getTime());
  const startMaxDateTime =
    isValidDateValue(endDateTimeValue) && endDateTimeValue.getTime() < now.getTime() ? endDateTimeValue : now;
  const areDatesEqual = (left: unknown, right: unknown): boolean =>
    left instanceof Date &&
    right instanceof Date &&
    !Number.isNaN(left.getTime()) &&
    !Number.isNaN(right.getTime()) &&
    left.getTime() === right.getTime();

  useEffect(() => {
    onValidityChange?.(formRenderProps.valid);
  }, [formRenderProps.valid, onValidityChange]);

  useEffect(() => {
    if (!selectedBandId || selectedBandStartTime == null || selectedBandEndTime == null) return;

    const lastSyncedFromForm = lastPeriodSyncedFromFormRef.current;
    if (
      lastSyncedFromForm &&
      lastSyncedFromForm.startTime === selectedBandStartTime &&
      lastSyncedFromForm.endTime === selectedBandEndTime
    ) {
      lastPeriodSyncedFromFormRef.current = null;
      return;
    }

    const nextStartDateTime = new Date(selectedBandStartTime * 1000);
    const nextEndDateTime = new Date(selectedBandEndTime * 1000);
    const currentStartDateTime = formRenderProps.valueGetter('startDateTime');
    const currentEndDateTime = formRenderProps.valueGetter('endDateTime');

    if (!areDatesEqual(currentStartDateTime, nextStartDateTime)) {
      formRenderProps.onChange('startDateTime', { value: nextStartDateTime });
    }

    if (!areDatesEqual(currentEndDateTime, nextEndDateTime)) {
      formRenderProps.onChange('endDateTime', { value: nextEndDateTime });
    }
  }, [formRenderProps, selectedBandEndTime, selectedBandId, selectedBandStartTime]);

  useEffect(() => {
    if (!selectedBandId) return;

    const data = {
      streams: streamsValue,
      ...(isAnchorBand
        ? {}
        : {
            criticality: criticalityValue,
            indicators: indicatorsValue,
            rootCauses: rootCausesValue,
            actions: actionsValue,
            confidence: confidenceValue,
            savings: savingsValue,
            ongoing: ongoingValue,
          }),
    };

    const valuesStr = JSON.stringify(data);
    if (prevValuesRef.current === valuesStr) return;

    prevValuesRef.current = valuesStr;

    syncFormToBands(data);
  }, [
    confidenceValue,
    isAnchorBand,
    rootCausesValue,
    actionsValue,
    savingsValue,
    selectedBandId,
    criticalityValue,
    streamsValue,
    indicatorsValue,
    ongoingValue,
    syncFormToBands,
  ]);

  const handlePeriodValueChange = (
    fieldName: 'startDateTime' | 'endDateTime',
    nextValue: Date | null | undefined,
    ongoingOverride?: boolean
  ) => {
    if (!selectedBandId) return;

    const nextStartDateTime = fieldName === 'startDateTime' ? nextValue : startDateTimeValue;
    const nextEndDateTime = fieldName === 'endDateTime' ? nextValue : endDateTimeValue;

    if (
      !isValidDateValue(nextStartDateTime) ||
      !isValidDateValue(nextEndDateTime) ||
      nextStartDateTime.getTime() > nextEndDateTime.getTime() ||
      isFutureDate(nextStartDateTime) ||
      isFutureDate(nextEndDateTime)
    ) {
      return;
    }

    lastPeriodSyncedFromFormRef.current = {
      startTime: Math.floor(nextStartDateTime.getTime() / 1000),
      endTime: Math.floor(nextEndDateTime.getTime() / 1000),
    };

    syncFormToBands({
      startDateTime: nextStartDateTime,
      endDateTime: nextEndDateTime,
      streams: streamsValue,
      ...(isAnchorBand
        ? {}
        : {
            criticality: criticalityValue,
            indicators: indicatorsValue,
            rootCauses: rootCausesValue,
            actions: actionsValue,
            confidence: confidenceValue,
            savings: savingsValue,
            ongoing: ongoingOverride ?? ongoingValue,
          }),
    });
  };

  const handleOngoingToggle = (checked: boolean) => {
    if (!selectedBandId) return;
    formRenderProps.onChange('ongoing', { value: checked });
    if (!checked) {
      // "Set end date" enabled — seed the end date with the current date/time
      // and sync it onto the band so it persists if left unedited.
      const now = new Date();
      formRenderProps.onChange('endDateTime', { value: now });
      handlePeriodValueChange('endDateTime', now, false);
    }
  };

  const setEndDateLabel = localizationService.toLanguageString('custom.setEndDate', enMessages.custom.setEndDate);
  const endText = localizationService.toLanguageString('custom.end', enMessages.custom.end);

  if (!selectedBandId) {
    return (
      <div className="flex min-h-[320px] items-center justify-center px-6 py-10 text-center">
        <div className="max-w-sm">
          <div className="text-base font-medium text-gray-900">
            {localizationService.toLanguageString('custom.noBandSelected', enMessages.custom.noBandSelected)}
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {localizationService.toLanguageString(
              'custom.selectBandToEditOrCreate',
              enMessages.custom.selectBandToEditOrCreate
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-2 pt-2">
      <fieldset
        className="k-form-fieldset"
        style={{ display: 'flex', flexDirection: 'column', gap: FIELD_SPACING, marginTop: FIELD_SPACING }}
      >
          <div className="flex flex-col" style={{ gap: isAnchorBand ? FIELD_SPACING : '0.75rem' }}>
            <Field
              name="startDateTime"
              label={`${localizationService.toLanguageString('custom.start', enMessages.custom.start)} *`}
              component={FormDateTimePicker}
              validator={startDateTimeValidator}
              validatorRequired={true}
              disabled={isDisabled}
              max={startMaxDateTime}
              onValueChange={(value: Date | null | undefined) => handlePeriodValueChange('startDateTime', value)}
            />
            {isAnchorBand ? (
              // Anchors have no "ongoing" concept — show the end date directly (no checkbox, no extra gap).
              <Field
                name="endDateTime"
                label={`${endText} *`}
                component={FormDateTimePicker}
                validator={endDateTimeValidator}
                validatorRequired={true}
                disabled={isDisabled}
                min={isValidDateValue(startDateTimeValue) ? startDateTimeValue : undefined}
                max={now}
                onValueChange={(value: Date | null | undefined) => handlePeriodValueChange('endDateTime', value)}
              />
            ) : (
              <div className="flex flex-col">
                <Checkbox
                  label={setEndDateLabel}
                  checked={!ongoingValue}
                  disabled={isDisabled}
                  onChange={(event) => handleOngoingToggle(event.value !== true)}
                />
                {ongoingValue ? (
                  // Ongoing anomalies have no end date — show a greyed-out field pinned to the current date/time.
                  <FieldWrapper>
                    <Label editorDisabled className="k-disabled">{`${endText} *`}</Label>
                    <DateTimePicker
                      format="dd/MM/yyyy HH:mm"
                      value={new Date()}
                      disabled
                    />
                  </FieldWrapper>
                ) : (
                  <Field
                    name="endDateTime"
                    label={`${endText} *`}
                    component={FormDateTimePicker}
                    validator={endDateTimeValidator}
                    validatorRequired={true}
                    disabled={isDisabled}
                    min={isValidDateValue(startDateTimeValue) ? startDateTimeValue : undefined}
                    max={now}
                    onValueChange={(value: Date | null | undefined) => handlePeriodValueChange('endDateTime', value)}
                  />
                )}
              </div>
            )}
          </div>
          <Field
            name="streams"
            label={`${localizationService.toLanguageString('custom.streams', enMessages.custom.streams)} *`}
            component={FormMultiSelect}
            validator={streamsRequiredValidator}
            validatorRequired={true}
            disabled={isDisabled}
            data={localizedStreamOptions}
            textField="text"
            dataItemKey="value"
            itemRender={renderItemWithCheckbox}
            suffix={() => (
              <InputSuffix orientation="horizontal">
                <Button themeColor="base" fillMode="flat" rounded={null} svgIcon={chevronDownIcon} />
              </InputSuffix>
            )}
            popupSettings={{ className: 'my-multiselect-popup' }}
            autoClose={false}
          />
          {!isAnchorBand && (
            <>
          <Field
            name="criticality"
            label={`${localizationService.toLanguageString('custom.criticality', enMessages.custom.criticality)} *`}
            component={FormSegmentedOptions}
            data={criticalityOptions}
            validator={criticalityRequiredValidator}
            validatorRequired={true}
            disabled={isDisabled}
          />
          <Field
            name="indicators"
            label={localizationService.toLanguageString('custom.indicator', enMessages.custom.indicator)}
            component={FormMultiSelect}
            data={normalizedIndicatorOptions}
            textField="text"
            dataItemKey="value"
            disabled={isDisabled}
            itemRender={renderItemWithCheckbox}
            suffix={() => (
              <InputSuffix orientation="horizontal">
                <Button themeColor="base" fillMode="flat" rounded={null} svgIcon={chevronDownIcon} />
              </InputSuffix>
            )}
            popupSettings={{ className: 'my-multiselect-popup' }}
            autoClose={false}
            onCustomValueAdded={onIndicatorCustomAdded ?? (() => {})}
            allowCustom={true}
            filterable={true}
          />
          <Field
            name="rootCauses"
            label={localizationService.toLanguageString('custom.rootCause', enMessages.custom.rootCause)}
            component={FormMultiSelect}
            data={normalizedRootCauseOptions}
            textField="text"
            dataItemKey="value"
            disabled={isDisabled}
            itemRender={renderItemWithCheckbox}
            suffix={() => (
              <InputSuffix orientation="horizontal">
                <Button themeColor="base" fillMode="flat" rounded={null} svgIcon={chevronDownIcon} />
              </InputSuffix>
            )}
            popupSettings={{ className: 'my-multiselect-popup' }}
            autoClose={false}
            onCustomValueAdded={onRootCauseCustomAdded ?? (() => {})}
            allowCustom={true}
            filterable={true}
          />
          <Field
            name="actions"
            label={localizationService.toLanguageString('custom.action', enMessages.custom.action)}
            component={FormMultiSelect}
            data={normalizedActionOptions}
            textField="text"
            dataItemKey="value"
            disabled={isDisabled}
            itemRender={renderItemWithCheckbox}
            suffix={() => (
              <InputSuffix orientation="horizontal">
                <Button themeColor="base" fillMode="flat" rounded={null} svgIcon={chevronDownIcon} />
              </InputSuffix>
            )}
            popupSettings={{ className: 'my-multiselect-popup' }}
            autoClose={false}
            onCustomValueAdded={onActionCustomAdded ?? (() => {})}
            allowCustom={true}
            filterable={true}
          />
          <Field
            name="confidence"
            label={localizationService.toLanguageString('custom.confidence', enMessages.custom.confidence)}
            component={FormSegmentedOptions}
            data={CONFIDENCE_OPTIONS}
            disabled={isDisabled}
          />
          <Field
            name="savings"
            label={localizationService.toLanguageString('custom.estimatedSavings', enMessages.custom.estimatedSavings)}
            component={FormNumericTextBox}
            format="n2"
            min={0}
            disabled={isDisabled}
          />
            </>
          )}
          {selectedBand?.createdByUserName && (
            <div className="mt-4 border-t border-gray-100 pt-3 text-xs text-gray-500">
              <span className="uppercase tracking-wide text-gray-400">
                {localizationService.toLanguageString('custom.createdBy', enMessages.custom.createdBy)}
              </span>
              <span className="ml-2 text-gray-600">{selectedBand.createdByUserName}</span>
            </div>
          )}
      </fieldset>
    </div>
  );
}
